/**
 * Platform Realtime — RealtimeCore (núcleo de cliente, agnóstico).
 *
 * ÚNICO ciclo de vida realtime de la plataforma (Kimberly Fashion primero,
 * CloudX después). Responsabilidades:
 *  - ciclo de vida del socket (creación, conexión, reconexión)
 *  - suscripciones EN COLA: nunca se descartan silenciosamente (si `on()` se
 *    llama antes de `connect()`, la suscripción queda pendiente y se aplica al
 *    conectar)
 *  - rejoin de rooms al reconectar (rooms registradas con joinRoom())
 *  - resync automático al reconectar (handlers registrados con onResync())
 *  - protección de eventos stale y fuera de orden (via realtimeContract)
 *  - auth reevaluada en cada reconexión (función de token)
 */
import { io } from 'socket.io-client';

export class RealtimeCore {
  /**
   * @param {string} namespace  '/client' | '/operator'
   * @param {object} opts       { getToken?: () => string|null, transports?: string[] }
   */
  constructor(namespace, opts = {}) {
    this.namespace = namespace;
    this.opts = opts;
    this.socket = null;
    this.subs = new Map();          // event -> Set<handler>
    this.dispatchFns = new Map();   // event -> fn estable (para off())
    this.rooms = new Set();         // rooms a (re)unir
    this.resyncHandlers = new Set(); // handlers de resync (se ejecutan en RECONEXIÓN)
    this.everConnected = false;
    this.connected = false;
    this.pendingOns = [];           // suscripciones encoladas (socket aún no creado)
    this.pendingJoins = [];         // joins encolados
    this.authErrorHandlers = new Set();
    this._authAttached = false;
  }

  connect() {
    if (this.socket) return this.socket;
    this._authAttached = false;
    this.socket = io(this.namespace, {
      path: '/socket.io',
      transports: this.opts.transports || ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // auth como FUNCIÓN: se reevalúa en cada (re)conexión → tokens renovados
      auth: (cb) => cb({ token: this.opts.getToken ? this.opts.getToken() : undefined })
    });

    this.socket.on('connect', () => {
      this.connected = true;
      const wasReconnect = this.everConnected;
      this.everConnected = true;
      // Rejoin de rooms registradas (cart:<id>, etc.)
      this.rooms.forEach((room) => {
        this.socket.emit('join-cart', { cartId: room });
      });
      this.pendingJoins.forEach((room) => this.socket.emit('join-cart', { cartId: room }));
      this.pendingJoins = [];
      // Suscripciones encoladas (nunca descartadas)
      this.flushPending();
      // Resync automático tras RECONEXIÓN (recupera eventos perdidos)
      if (wasReconnect) {
        this.resyncHandlers.forEach((h) => {
          try { h(); } catch (e) { console.error('[RealtimeCore] resync error:', e); }
        });
      }
    });

    this.socket.on('disconnect', () => {
      this.connected = false;
    });

    return this.socket;
  }

  flushPending() {
    if (!this.socket) return;
    this.pendingOns.forEach(({ event, handler }) => {
      if (!this.subs.has(event)) {
        this.subs.set(event, new Set());
        const dispatch = this._dispatch(event);
        this.dispatchFns.set(event, dispatch);
        this.socket.on(event, dispatch);
      }
      this.subs.get(event).add(handler);
    });
    this.pendingOns = [];
  }

  /** Suscripción con cola: NUNCA se descarta silenciosamente. */
  on(event, handler) {
    if (!this.socket) {
      this.pendingOns.push({ event, handler });
      return () => this.off(event, handler);
    }
    if (!this.subs.has(event)) {
      this.subs.set(event, new Set());
      const dispatch = this._dispatch(event);
      this.dispatchFns.set(event, dispatch);
      this.socket.on(event, dispatch);
    }
    this.subs.get(event).add(handler);
    return () => this.off(event, handler);
  }

  off(event, handler) {
    const set = this.subs.get(event);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0 && this.socket) {
      const dispatch = this.dispatchFns.get(event);
      if (dispatch) this.socket.off(event, dispatch);
      this.dispatchFns.delete(event);
      this.subs.delete(event);
    }
  }

  _dispatch(event) {
    const fn = (data) => {
      const set = this.subs.get(event);
      if (!set) return;
      set.forEach((h) => {
        try { h(data); } catch (e) { console.error('[RealtimeCore] handler error (' + event + '):', e); }
      });
    };
    fn._coreDispatch = true;
    return fn;
  }

  /** Registra una room para (re)unirla en cada conexión. */
  joinRoom(room) {
    this.rooms.add(room);
    if (this.socket && this.connected) this.socket.emit('join-cart', { cartId: room });
  }

  leaveRoom(room) {
    this.rooms.delete(room);
    if (this.socket && this.connected) this.socket.emit('leave-cart', { cartId: room });
  }

  /** Registra un handler de resync que se ejecuta SOLO en reconexiones. */
  onResync(handler) {
    this.resyncHandlers.add(handler);
    return () => this.resyncHandlers.delete(handler);
  }

  /** Handler de rechazo de sesión (connect_error del middleware de auth). */
  onAuthError(handler) {
    this.authErrorHandlers.add(handler);
    if (!this._authAttached && this.socket) {
      this._authAttached = true;
      this.socket.on('connect_error', (err) => {
        const msg = err && err.message ? err.message : '';
        if (/token|sesi|session|auth/i.test(msg)) {
          this.authErrorHandlers.forEach((h) => { try { h(msg); } catch (e) { console.error('[RealtimeCore] authError handler error:', e); } });
        }
      });
    }
    return () => this.authErrorHandlers.delete(handler);
  }

  emit(event, data) {
    if (this.socket && this.connected) this.socket.emit(event, data);
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.connected = false;
    this.everConnected = false;
    this.subs.clear();
    this.dispatchFns.clear();
    this.rooms.clear();
    this.pendingOns = [];
    this.pendingJoins = [];
  }
}

export default RealtimeCore;
